import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import API from "../config";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const emptyForm = {
  name: "",
  email: "",
  password: "",
  deviceIds: [],
};

const getUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

const ESGReports = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [admins, setAdmins] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selectedAdminId, setSelectedAdminId] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const user = getUser();
  const role = (user?.role || "").toLowerCase();
  const isSuperAdmin = role === "super_admin";

  const selectedAdmin = useMemo(
    () => admins.find((admin) => String(admin.id) === String(selectedAdminId)),
    [admins, selectedAdminId]
  );

  const assignedDevices = useMemo(() => {
    const assignedIds = new Set((selectedAdmin?.device_ids || []).map(Number));
    return isSuperAdmin
      ? devices.filter((device) => assignedIds.has(Number(device.id)))
      : devices;
  }, [devices, isSuperAdmin, selectedAdmin]);

  const fetchDashboard = useCallback(async (adminId = null) => {
    setError("");
    try {
      const url = adminId
        ? `${API}/esg/dashboard?userId=${adminId}`
        : `${API}/esg/dashboard`;
      const response = await fetch(url, { headers: authHeaders() });
      const result = await response.json();

      if (!response.ok) throw new Error(result.message || "Failed to load ESG dashboard");
      setData(result);
    } catch (err) {
      setData(null);
      setError(err.message || "Server connection error");
    }
  }, []);

  const fetchAdmins = useCallback(async () => {
    const response = await fetch(`${API}/esg/admins`, { headers: authHeaders() });
    const result = await response.json();
    if (!response.ok) throw new Error(result.message || "Failed to load ESG admins");
    setAdmins(result);

    if (!selectedAdminId && result.length) {
      setSelectedAdminId(String(result[0].id));
      await fetchDashboard(result[0].id);
    }
  }, [fetchDashboard, selectedAdminId]);

  const fetchDevices = useCallback(async () => {
    const response = await fetch(`${API}/esg/devices`, { headers: authHeaders() });
    const result = await response.json();
    if (!response.ok) throw new Error(result.message || "Failed to load devices");
    setDevices(result);
  }, []);

  const loadPage = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (isSuperAdmin) {
        await Promise.all([fetchDevices(), fetchAdmins()]);
      } else {
        await Promise.all([fetchDevices(), fetchDashboard()]);
      }
    } catch (err) {
      setError(err.message || "Failed to load ESG page");
    } finally {
      setLoading(false);
    }
  }, [fetchAdmins, fetchDashboard, fetchDevices, isSuperAdmin]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const handleSelectAdmin = async (adminId) => {
    setSelectedAdminId(adminId);
    setSuccess("");
    if (adminId) await fetchDashboard(adminId);
    else setData(null);
  };

  const toggleDevice = (deviceId) => {
    setForm((current) => {
      const id = Number(deviceId);
      const ids = new Set(current.deviceIds.map(Number));
      if (ids.has(id)) {
        ids.delete(id);
      } else {
        ids.add(id);
      }
      return { ...current, deviceIds: [...ids] };
    });
  };

  const openAssignModal = () => {
    if (!selectedAdmin) return;
    setForm({ ...emptyForm, deviceIds: selectedAdmin.device_ids || [] });
    setShowAssignModal(true);
    setSuccess("");
  };

  const createAdmin = async () => {
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`${API}/esg/admins`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          password: form.password,
          deviceIds: form.deviceIds,
        }),
      });
      const result = await response.json();

      if (!response.ok) throw new Error(result.message || "Failed to create ESG admin");

      setShowCreateModal(false);
      setForm(emptyForm);
      setSuccess("ESG admin created and devices assigned.");
      await fetchAdmins();
      await handleSelectAdmin(String(result.id));
    } catch (err) {
      setError(err.message || "Failed to create ESG admin");
    } finally {
      setSaving(false);
    }
  };

  const saveAssignments = async () => {
    if (!selectedAdmin) return;
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`${API}/esg/admins/${selectedAdmin.id}/devices`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ deviceIds: form.deviceIds }),
      });
      const result = await response.json();

      if (!response.ok) throw new Error(result.message || "Failed to update devices");

      setShowAssignModal(false);
      setSuccess("Device access updated.");
      await fetchAdmins();
      await fetchDashboard(selectedAdmin.id);
    } catch (err) {
      setError(err.message || "Failed to update devices");
    } finally {
      setSaving(false);
    }
  };

  const exportCSV = () => {
    if (!data?.monthly?.length) return;

    const rows = [
      ["Month", "CO2 (Tons)", "Energy (kWh)", "Efficiency (%)"],
      ...data.monthly.map((month) => [
        month.month,
        month.co2,
        month.energy,
        month.efficiency,
      ]),
    ];

    const blob = new Blob([rows.map((row) => row.join(",")).join("\n")], {
      type: "text/csv",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "esg_report.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="p-6 text-gray-500">Loading ESG dashboard...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-green-700">ESG Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">
            Sponsor device performance, carbon savings, and monthly ESG activity.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          {isSuperAdmin && (
            <>
              <button
                onClick={() => {
                  setForm(emptyForm);
                  setShowCreateModal(true);
                  setSuccess("");
                }}
                className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700"
              >
                + Add ESG Admin
              </button>

              <select
                className="border px-3 py-2 rounded-md text-sm bg-white min-w-[190px]"
                value={selectedAdminId}
                onChange={(event) => handleSelectAdmin(event.target.value)}
              >
                <option value="">Select ESG Admin</option>
                {admins.map((admin) => (
                  <option key={admin.id} value={admin.id}>
                    {admin.name}
                  </option>
                ))}
              </select>

              <button
                onClick={openAssignModal}
                disabled={!selectedAdmin}
                className="border px-3 py-2 rounded-md text-sm hover:bg-gray-50 disabled:opacity-40"
              >
                Assign Devices
              </button>
            </>
          )}

          <button
            onClick={() => (isSuperAdmin ? fetchDashboard(selectedAdminId) : fetchDashboard())}
            disabled={isSuperAdmin && !selectedAdminId}
            className="border px-3 py-2 rounded-md text-sm hover:bg-gray-50 disabled:opacity-40"
          >
            Refresh
          </button>

          <button
            onClick={exportCSV}
            disabled={!data?.monthly?.length}
            className="border px-3 py-2 rounded-md text-sm hover:bg-gray-50 disabled:opacity-40"
          >
            Export CSV
          </button>

          <button
            onClick={() => window.print()}
            className="border px-3 py-2 rounded-md text-sm hover:bg-gray-50"
          >
            Export PDF
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {success}
        </div>
      )}

      {isSuperAdmin && (
        <section className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
          <div className="bg-white border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700">ESG Admins</h2>
              <span className="text-xs text-gray-400">{admins.length} admins</span>
            </div>

            <div className="space-y-2 max-h-[360px] overflow-auto">
              {admins.length ? (
                admins.map((admin) => (
                  <button
                    key={admin.id}
                    onClick={() => handleSelectAdmin(String(admin.id))}
                    className={`w-full text-left border rounded-md p-3 transition ${
                      String(selectedAdminId) === String(admin.id)
                        ? "border-green-500 bg-green-50"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="font-medium text-gray-900">{admin.name}</div>
                    <div className="text-xs text-gray-500 truncate">{admin.email}</div>
                    <div className="text-xs text-green-700 mt-1">
                      {admin.device_count || 0} sponsored devices
                    </div>
                  </button>
                ))
              ) : (
                <p className="text-sm text-gray-400">No ESG admins created yet.</p>
              )}
            </div>
          </div>

          <DeviceAccessPanel devices={assignedDevices} />
        </section>
      )}

      {!isSuperAdmin && <DeviceAccessPanel devices={assignedDevices} />}

      {isSuperAdmin && !selectedAdminId && (
        <EmptyState message="Select or create an ESG admin to view dashboard performance." />
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <MetricCard title="CO2 Reduced" value={`${data.total_co2 ?? 0} Tons`} />
            <MetricCard title="Energy Saved" value={`${data.energy_saved ?? 0} kWh`} />
            <MetricCard title="Carbon Credits" value={data.carbon_credits ?? 0} />
            <MetricCard title="Devices" value={data.devices ?? 0} />
            <MetricCard title="ESG Score" value={`${data.score ?? 0} / 100`} />
          </div>

          {data.monthly?.length ? (
            <section className="bg-white p-5 rounded-lg shadow-sm border">
              <h3 className="text-sm font-semibold text-gray-600 mb-3">
                Monthly CO2 Reduction
              </h3>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.monthly}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="co2"
                    stroke="#16a34a"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </section>
          ) : (
            <EmptyState message="No monthly data available yet." />
          )}

          {data.device_breakdown?.length > 0 && (
            <section className="bg-white p-5 rounded-lg border">
              <h3 className="text-sm font-semibold text-gray-600 mb-3">
                Device Monthly Performance
              </h3>
              <div className="overflow-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-gray-500 border-b">
                    <tr>
                      <th className="py-2 pr-4">Device</th>
                      <th className="py-2 pr-4">CO2</th>
                      <th className="py-2 pr-4">Energy</th>
                      <th className="py-2 pr-4">Efficiency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.device_breakdown.map((item, index) => (
                      <tr key={`${item.device_uid || item.device || index}`} className="border-b last:border-0">
                        <td className="py-2 pr-4 font-medium text-gray-800">
                          {item.device_uid || item.device || "Device"}
                        </td>
                        <td className="py-2 pr-4">{item.co2 ?? 0}</td>
                        <td className="py-2 pr-4">{item.energy ?? 0}</td>
                        <td className="py-2 pr-4">{item.efficiency ?? "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {data.insights?.length > 0 && (
            <section className="bg-white p-5 rounded-lg border space-y-2">
              <h3 className="text-sm font-semibold text-gray-600">Insights</h3>
              {data.insights.map((insight, index) => (
                <p key={`${insight}-${index}`} className="text-sm text-gray-700">
                  {insight}
                </p>
              ))}
            </section>
          )}
        </>
      )}

      {showCreateModal && (
        <AdminModal
          title="Create ESG Admin"
          form={form}
          devices={devices}
          saving={saving}
          onChange={setForm}
          onToggleDevice={toggleDevice}
          onCancel={() => setShowCreateModal(false)}
          onSubmit={createAdmin}
          submitLabel="Create Admin"
        />
      )}

      {showAssignModal && (
        <AdminModal
          title={`Assign Devices${selectedAdmin ? ` - ${selectedAdmin.name}` : ""}`}
          form={form}
          devices={devices}
          saving={saving}
          onChange={setForm}
          onToggleDevice={toggleDevice}
          onCancel={() => setShowAssignModal(false)}
          onSubmit={saveAssignments}
          submitLabel="Save Devices"
          assignmentOnly
        />
      )}
    </div>
  );
};

const MetricCard = ({ title, value }) => (
  <div className="bg-white p-5 rounded-lg shadow-sm border text-center">
    <p className="text-gray-500 text-sm">{title}</p>
    <h2 className="text-xl font-bold mt-2 text-green-600">{value}</h2>
  </div>
);

const EmptyState = ({ message }) => (
  <div className="p-7 text-center text-gray-400 bg-white rounded-lg border">
    {message}
  </div>
);

const DeviceAccessPanel = ({ devices }) => (
  <section className="bg-white border rounded-lg p-4">
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-sm font-semibold text-gray-700">Sponsored Devices</h2>
      <span className="text-xs text-gray-400">{devices.length} devices</span>
    </div>

    {devices.length ? (
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {devices.map((device) => (
          <div key={device.id} className="border rounded-md p-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-semibold text-gray-900">{device.device_uid}</div>
                <div className="text-xs text-gray-500">
                  {device.company_name || "No company"} {device.product_name ? `- ${device.product_name}` : ""}
                </div>
              </div>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-50 text-green-700">
                {device.status || "active"}
              </span>
            </div>
            <Link
              to={`/dashboard/devices/${device.device_uid}`}
              className="inline-block mt-3 text-sm text-blue-600 hover:underline"
            >
              View standalone
            </Link>
          </div>
        ))}
      </div>
    ) : (
      <p className="text-sm text-gray-400">
        No sponsored devices assigned yet.
      </p>
    )}
  </section>
);

const AdminModal = ({
  title,
  form,
  devices,
  saving,
  onChange,
  onToggleDevice,
  onCancel,
  onSubmit,
  submitLabel,
  assignmentOnly = false,
}) => {
  const groupedDevices = useMemo(() => {
    return devices.reduce((groups, device) => {
      const group = device.company_name || "No company";
      groups[group] = groups[group] || [];
      groups[group].push(device);
      return groups;
    }, {});
  }, [devices]);

  const selectedIds = new Set((form.deviceIds || []).map(Number));
  const canSubmit = assignmentOnly || (form.name && form.email && form.password);

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl shadow-xl max-h-[88vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-700">
            Close
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-auto">
          {!assignmentOnly && (
            <div className="grid sm:grid-cols-3 gap-3">
              <input
                placeholder="Name"
                value={form.name}
                onChange={(event) => onChange({ ...form, name: event.target.value })}
                className="w-full border p-2 rounded-md text-sm"
              />
              <input
                placeholder="Email"
                value={form.email}
                onChange={(event) => onChange({ ...form, email: event.target.value })}
                className="w-full border p-2 rounded-md text-sm"
              />
              <input
                type="password"
                placeholder="Password"
                value={form.password}
                onChange={(event) => onChange({ ...form, password: event.target.value })}
                className="w-full border p-2 rounded-md text-sm"
              />
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700">Device Access</h3>
              <span className="text-xs text-gray-400">{selectedIds.size} selected</span>
            </div>

            <div className="border rounded-lg divide-y max-h-[420px] overflow-auto">
              {Object.entries(groupedDevices).map(([company, companyDevices]) => (
                <div key={company} className="p-3">
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                    {company}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {companyDevices.map((device) => (
                      <label
                        key={device.id}
                        className="flex items-center gap-2 border rounded-md px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.has(Number(device.id))}
                          onChange={() => onToggleDevice(device.id)}
                        />
                        <span className="font-medium text-gray-800">{device.device_uid}</span>
                        <span className="text-xs text-gray-400 truncate">
                          {device.product_name || "Device"}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              {!devices.length && (
                <p className="p-4 text-sm text-gray-400">No devices available.</p>
              )}
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t flex justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-2 border rounded-md text-sm">
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={!canSubmit || saving}
            className="bg-green-600 text-white px-3 py-2 rounded-md text-sm disabled:opacity-50"
          >
            {saving ? "Saving..." : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ESGReports;
